import { computed, inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { LegacyPreferenceStorageService } from '../../realtime/legacy-preference-storage.service';
import {
  buildCarTypesFromTariffs,
  buildLookupMap,
  CarTypeRecord,
  CountryCode,
  CountryDefinition,
  deriveAraTariffIds,
  deriveBreadTariffIds,
  deriveTruckTariffIds,
  deriveEffectiveCarTypeIds,
  deriveEffectiveRegionIds,
  deriveSelectedTariffIds,
  getCountryDefinition,
  hasNoFilterSelection,
  LEGACY_COUNTRY_DEFINITIONS,
  matchesOrderToSelectedTariffs,
  NO_FILTER_SELECTION_ID,
  normalizeLegacyCarTypeSelectionIds,
  normalizeLegacyRegionSelectionIds
} from './reference-data.derived';

export interface RegionRecord {
  id: number;
  name: string;
  childIds: number[];
  coordinates: Array<[number, number]>;
  state: string | null;
}

export interface TariffRecord {
  id: number;
  name: string;
  typeId: number | null;
  regionId: number | null;
  /** Car type name from the backend (e.g. "ggTruck M"); distinguishes types whose tariffs share a name. */
  typeName?: string | null;
}

export interface OptionRecord {
  id: number;
  name: string;
}

export interface ReferenceDataStateModel {
  regions: RegionRecord[];
  tariffs: TariffRecord[];
  options: OptionRecord[];
  countries: CountryDefinition[];
  selectedCountry: CountryCode;
  selectedRegionId: number;
  selectedRegionIds: number[];
  selectedCarTypeIds: number[];
  selectedTariffIds: number[];
  loadedAt: string | null;
}

export interface MonitoringSelectionPayload {
  selectedRegionIds: number[];
  selectedCarTypeIds: number[];
}

export interface ReferencePreferencesPayload {
  selectedCountry: CountryCode;
  selectedRegionId: number;
  selectedRegionIds: number[];
  selectedCarTypeIds: number[];
}

const REFERENCE_DATA_STATE_DEFAULTS: ReferenceDataStateModel = {
  regions: [],
  tariffs: [],
  options: [],
  countries: LEGACY_COUNTRY_DEFINITIONS,
  selectedCountry: 'AM',
  selectedRegionId: 0,
  selectedRegionIds: [],
  selectedCarTypeIds: [],
  selectedTariffIds: [],
  loadedAt: null
};

export class SetReferenceData {
  static readonly type = '[Reference Data] Set';

  constructor(public readonly payload: Partial<ReferenceDataStateModel>) {}
}

export class RestoreReferencePreferences {
  static readonly type = '[Reference Data] Restore Preferences';

  constructor(public readonly payload: ReferencePreferencesPayload) {}
}

export class SetSelectedCountry {
  static readonly type = '[Reference Data] Set Selected Country';

  constructor(public readonly selectedCountry: CountryCode) {}
}

export class SetSelectedRegionId {
  static readonly type = '[Reference Data] Set Selected Region Id';

  constructor(public readonly selectedRegionId: number) {}
}

export class SetMonitoringSelections {
  static readonly type = '[Reference Data] Set Monitoring Selections';

  constructor(public readonly payload: MonitoringSelectionPayload) {}
}

export class ClearReferenceData {
  static readonly type = '[Reference Data] Clear';
}

@State<ReferenceDataStateModel>({
  name: 'referenceData',
  defaults: REFERENCE_DATA_STATE_DEFAULTS
})
@Injectable()
export class ReferenceDataState {
  @Selector()
  static snapshot(state: ReferenceDataStateModel): ReferenceDataStateModel {
    return state;
  }

  @Selector()
  static regions(state: ReferenceDataStateModel): RegionRecord[] {
    return state.regions;
  }

  @Selector()
  static tariffs(state: ReferenceDataStateModel): TariffRecord[] {
    return state.tariffs;
  }

  @Selector()
  static options(state: ReferenceDataStateModel): OptionRecord[] {
    return state.options;
  }

  @Selector()
  static countries(state: ReferenceDataStateModel): CountryDefinition[] {
    return state.countries;
  }

  @Selector()
  static selectedCountry(state: ReferenceDataStateModel): CountryCode {
    return state.selectedCountry;
  }

  @Selector()
  static selectedRegionId(state: ReferenceDataStateModel): number {
    return state.selectedRegionId;
  }

  @Selector()
  static selectedRegionIds(state: ReferenceDataStateModel): number[] {
    return state.selectedRegionIds;
  }

  @Selector()
  static selectedCarTypeIds(state: ReferenceDataStateModel): number[] {
    return state.selectedCarTypeIds;
  }

  @Selector()
  static selectedTariffIds(state: ReferenceDataStateModel): number[] {
    return state.selectedTariffIds;
  }

  @Action(SetReferenceData)
  setReferenceData(ctx: StateContext<ReferenceDataStateModel>, action: SetReferenceData): void {
    const currentState = ctx.getState();
    ctx.setState(
      withDerivedSelections({
        ...currentState,
        ...action.payload,
        loadedAt: action.payload.loadedAt ?? new Date().toISOString()
      })
    );
  }

  @Action(RestoreReferencePreferences)
  restoreReferencePreferences(
    ctx: StateContext<ReferenceDataStateModel>,
    action: RestoreReferencePreferences
  ): void {
    const currentState = ctx.getState();
    ctx.setState(
      withDerivedSelections({
        ...currentState,
        selectedCountry: action.payload.selectedCountry,
        selectedRegionId: action.payload.selectedRegionId,
        selectedRegionIds: [...action.payload.selectedRegionIds],
        selectedCarTypeIds: [...action.payload.selectedCarTypeIds]
      })
    );
  }

  @Action(SetSelectedCountry)
  setSelectedCountry(ctx: StateContext<ReferenceDataStateModel>, action: SetSelectedCountry): void {
    ctx.patchState({
      selectedCountry: action.selectedCountry
    });
  }

  @Action(SetSelectedRegionId)
  setSelectedRegionId(ctx: StateContext<ReferenceDataStateModel>, action: SetSelectedRegionId): void {
    ctx.patchState({
      selectedRegionId: action.selectedRegionId
    });
  }

  @Action(SetMonitoringSelections)
  setMonitoringSelections(
    ctx: StateContext<ReferenceDataStateModel>,
    action: SetMonitoringSelections
  ): void {
    const currentState = ctx.getState();
    ctx.setState(
      withDerivedSelections({
        ...currentState,
        selectedRegionIds: [...action.payload.selectedRegionIds],
        selectedCarTypeIds: [...action.payload.selectedCarTypeIds]
      })
    );
  }

  @Action(ClearReferenceData)
  clearReferenceData(ctx: StateContext<ReferenceDataStateModel>): void {
    ctx.setState(REFERENCE_DATA_STATE_DEFAULTS);
  }
}

@Injectable({ providedIn: 'root' })
export class ReferenceDataFacade {
  private readonly store = inject(Store);
  private readonly preferenceStorage = inject(LegacyPreferenceStorageService);

  readonly referenceData = this.store.selectSignal(ReferenceDataState.snapshot);
  readonly regions = this.store.selectSignal(ReferenceDataState.regions);
  readonly tariffs = this.store.selectSignal(ReferenceDataState.tariffs);
  readonly options = this.store.selectSignal(ReferenceDataState.options);
  readonly countries = this.store.selectSignal(ReferenceDataState.countries);
  readonly selectedCountry = this.store.selectSignal(ReferenceDataState.selectedCountry);
  readonly selectedRegionId = this.store.selectSignal(ReferenceDataState.selectedRegionId);
  readonly selectedRegionIds = this.store.selectSignal(ReferenceDataState.selectedRegionIds);
  readonly selectedCarTypeIds = this.store.selectSignal(ReferenceDataState.selectedCarTypeIds);
  readonly storedSelectedTariffIds = this.store.selectSignal(ReferenceDataState.selectedTariffIds);
  readonly carTypes = computed<CarTypeRecord[]>(() => buildCarTypesFromTariffs(this.tariffs()));
  readonly regionLookup = computed<Record<number, RegionRecord>>(() => buildLookupMap(this.regions()));
  readonly tariffLookup = computed<Record<number, TariffRecord>>(() => buildLookupMap(this.tariffs()));
  readonly breadTariffIds = computed<number[]>(() => deriveBreadTariffIds(this.tariffs()));
  readonly truckTariffIds = computed<number[]>(() => deriveTruckTariffIds(this.tariffs()));
  readonly araTariffIds = computed<number[]>(() => deriveAraTariffIds(this.tariffs()));
  readonly optionLookup = computed<Record<number, OptionRecord>>(() => buildLookupMap(this.options()));
  readonly selectedCountryDefinition = computed<CountryDefinition>(() =>
    getCountryDefinition(this.selectedCountry())
  );
  readonly selectedTariffIds = computed<number[]>(() => {
    const selectedTariffIds = this.storedSelectedTariffIds();

    if (selectedTariffIds.length > 0) {
      return selectedTariffIds;
    }

    return deriveSelectedTariffIds(
      this.tariffs(),
      this.selectedRegionIds(),
      this.selectedCarTypeIds()
    );
  });
  readonly effectiveRegionIds = computed<number[]>(() =>
    deriveEffectiveRegionIds(this.regions(), this.selectedRegionIds())
  );
  readonly effectiveCarTypeIds = computed<number[]>(() =>
    deriveEffectiveCarTypeIds(this.carTypes(), this.selectedCarTypeIds())
  );

  constructor() {
    this.restorePreferencesFromLegacyStorage();
  }

  setReferenceData(payload: Partial<ReferenceDataStateModel>): void {
    this.store.dispatch(new SetReferenceData(payload));
  }

  setSelectedCountry(selectedCountry: CountryCode): void {
    this.store.dispatch(new SetSelectedCountry(selectedCountry));
    this.preferenceStorage.writeString('selectedCountry', selectedCountry);
  }

  setSelectedRegionId(selectedRegionId: number): void {
    this.store.dispatch(new SetSelectedRegionId(selectedRegionId));
    this.preferenceStorage.writeString('selectedRegionId', `${selectedRegionId}`);
  }

  setMonitoringSelections(selectedRegionIds: number[], selectedCarTypeIds: number[]): void {
    this.store.dispatch(
      new SetMonitoringSelections({
        selectedRegionIds,
        selectedCarTypeIds
      })
    );

    this.preferenceStorage.writeJson(
      'selectedRegionIds',
      toLegacyRegionSelections(selectedRegionIds, this.regionLookup())
    );
    this.preferenceStorage.writeJson(
      'selectedCarTypeIds',
      toLegacyCarTypeSelections(selectedCarTypeIds, this.carTypes())
    );
    this.preferenceStorage.writeJson(
      'selectedTariffIds',
      deriveSelectedTariffIds(this.tariffs(), selectedRegionIds, selectedCarTypeIds)
    );
  }

  matchesTariffId(tariffId: number | null | undefined): boolean {
    return matchesOrderToSelectedTariffs(
      {
        tariffId: tariffId ?? null
      },
      this.selectedTariffIds()
    );
  }

  clearReferenceData(): void {
    this.store.dispatch(new ClearReferenceData());
  }

  private restorePreferencesFromLegacyStorage(): void {
    this.store.dispatch(
      new RestoreReferencePreferences({
        selectedCountry: normalizeCountryCode(
          this.preferenceStorage.readString('selectedCountry', 'AM')
        ),
        selectedRegionId: this.preferenceStorage.readNumber('selectedRegionId', 0),
        selectedRegionIds: normalizeLegacyRegionSelectionIds(
          this.preferenceStorage.readJson('selectedRegionIds', [])
        ),
        selectedCarTypeIds: normalizeLegacyCarTypeSelectionIds(
          this.preferenceStorage.readJson('selectedCarTypeIds', [])
        )
      })
    );
  }
}

function withDerivedSelections(state: ReferenceDataStateModel): ReferenceDataStateModel {
  return {
    ...state,
    selectedTariffIds: deriveSelectedTariffIds(
      state.tariffs,
      state.selectedRegionIds,
      state.selectedCarTypeIds
    )
  };
}

function normalizeCountryCode(rawCountryCode: string): CountryCode {
  if (rawCountryCode === 'GE' || rawCountryCode === 'RU') {
    return rawCountryCode;
  }

  return 'AM';
}

function toLegacyRegionSelections(
  selectedRegionIds: number[],
  regionLookup: Record<number, RegionRecord>
): Array<{
  regionId: number;
  name: string;
  childs: number[];
  coordinates: Array<[number, number]>;
}> {
  if (hasNoFilterSelection(selectedRegionIds)) {
    return [{ regionId: NO_FILTER_SELECTION_ID, name: 'None', childs: [], coordinates: [] }];
  }

  return selectedRegionIds
    .map((regionId) => regionLookup[regionId])
    .filter((region): region is RegionRecord => !!region)
    .map((region) => ({
      regionId: region.id,
      name: region.name,
      childs: [...region.childIds],
      coordinates: [...region.coordinates]
    }));
}

function toLegacyCarTypeSelections(
  selectedCarTypeIds: number[],
  carTypes: CarTypeRecord[]
): Array<{
  typeId: number;
  name: string;
  tariffs: number[];
}> {
  if (hasNoFilterSelection(selectedCarTypeIds)) {
    return [{ typeId: NO_FILTER_SELECTION_ID, name: 'None', tariffs: [] }];
  }

  const carTypeLookup = carTypes.reduce<Record<number, CarTypeRecord>>((lookup, carType) => {
    lookup[carType.id] = carType;
    return lookup;
  }, {});

  return selectedCarTypeIds
    .map((carTypeId) => carTypeLookup[carTypeId])
    .filter((carType): carType is CarTypeRecord => !!carType)
    .map((carType) => ({
      typeId: carType.id,
      name: carType.name,
      tariffs: [...carType.tariffIds]
    }));
}
